import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Doc { id: number; fileName: string; fromAddress: string; receivedAt: string; supplier: string | null; number: string | null; total: number | null; note: string }
interface Pay { id: number; date: string; amount: number; counterparty: string; purpose: string; note: string }
interface Queue { pairs: { document: Doc; payment: Pay }[]; documents: Doc[]; payments: Pay[] }

/**
 * Buhalteriui: what the accountant has to sort out — documents from the e-mail inbox and bank payments without
 * their document. A document and a payment of the same amount are shown side by side.
 */
export function AccountantQueuePage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const queue = useQuery({ queryKey: ['accountant-queue'], queryFn: () => api.get<Queue>('api/v1/accountant-queue') });
  const refresh = () => Promise.all(['accountant-queue', 'inbox', 'bank'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
  const done = useMutation({ mutationFn: (body: { documentId?: number; paymentId?: number }) => api.post('api/v1/accountant-queue/done', body), onSuccess: refresh });
  const back = useMutation({ mutationFn: (id: number) => api.post(`api/v1/inbox/${id}/accountant`, { on: false }), onSuccess: refresh });
  const readOnly = Boolean(me.viewAsAdmin);
  const q = queue.data;
  const empty = q && !q.pairs.length && !q.documents.length && !q.payments.length;

  const doc = (d: Doc) => (
    <div className="acq-item">
      <span className="eyebrow">{t('Dokumentas')}</span>
      <Link to="/view" search={{ type: 'inbox', id: d.id, title: d.fileName }}><strong>{d.supplier ?? d.fileName}</strong></Link>
      <small>{[d.number, d.receivedAt.slice(0, 10), d.fromAddress].filter(Boolean).join(' · ')}</small>
      {d.total !== null ? <strong className="acq-sum">{money(d.total)}</strong> : <small className="muted">{t('Suma neperskaityta')}</small>}
      {d.note ? <small>{d.note}</small> : null}
      {!readOnly ? <button type="button" className="text-link" disabled={back.isPending} onClick={() => back.mutate(d.id)}>{t('Grąžinti į Gautas')}</button> : null}
    </div>
  );
  const pay = (p: Pay) => (
    <div className="acq-item">
      <span className="eyebrow">{t('Mokėjimas')}</span>
      <strong>{p.counterparty || '—'}</strong>
      <small>{[p.date, p.purpose].filter(Boolean).join(' · ')}</small>
      <strong className={`acq-sum${p.amount > 0 ? ' is-in' : ''}`}>{p.amount > 0 ? '+' : '−'}{money(Math.abs(p.amount))}</strong>
      {p.note ? <small>{p.note}</small> : null}
    </div>
  );
  const doneButton = (body: { documentId?: number; paymentId?: number }) => (!readOnly
    ? <button type="button" className="button button--small button--primary" disabled={done.isPending} onClick={() => done.mutate(body)}>✓ {t('Sutvarkyta')}</button> : null);

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Buhalteriui')} text={t('Ką sutvarko buhalteris: dokumentai iš el. pašto ir mokėjimai be dokumento. Tos pačios sumos dokumentas ir mokėjimas rodomi kartu.')} />}>
      {queue.isError || done.isError || back.isError ? <Alert kind="error">{errorText(t, queue.error ?? done.error ?? back.error)}</Alert> : null}
      {empty ? <section className="panel-card"><p className="muted">{t('Buhalteriui nieko nelaukia. Dokumentą siųskite iš Gautos el. paštu („Buhalteriui“), mokėjimą — Banke priežastimi „Perduota buhalteriui“.')}</p></section> : null}
      {q?.pairs.length ? <section className="panel-card">
        <h2>{t('Dokumentas ir mokėjimas')}</h2>
        {q.pairs.map(({ document: d, payment: p }) => (
          <div key={`${d.id}-${p.id}`} className="acq-row">{doc(d)}<span className="acq-link" aria-hidden="true">↔</span>{pay(p)}{doneButton({ documentId: d.id, paymentId: p.id })}</div>
        ))}
      </section> : null}
      {q?.documents.length ? <section className="panel-card">
        <h2>{t('Dokumentai be mokėjimo')}</h2>
        {q.documents.map((d) => <div key={d.id} className="acq-row">{doc(d)}{doneButton({ documentId: d.id })}</div>)}
      </section> : null}
      {q?.payments.length ? <section className="panel-card">
        <h2>{t('Mokėjimai be dokumento')}</h2>
        {q.payments.map((p) => <div key={p.id} className="acq-row">{pay(p)}{doneButton({ paymentId: p.id })}</div>)}
      </section> : null}
    </PanelShell>
  );
}
